import { forwardRef, useState, useEffect } from "react";
import LinePlot from "../line-plot/line-plot.component";
import BarPlot from "../bar-plot/bar-plot.component";
import InvestigationsPlot from "../investigations-plot/investigations-plot.component";
import { useResize } from "../../assets/custom-hooks/resize";
import { createStyles } from "@mantine/core";
import { objectsAreEqual, isEmpty } from "../../assets/constants";
import SvgPlotWrapper from "../svg-plot-wrapper/svg-plot-wrapper.component";
import LogoWithText from "../logo-with-text/logo-with-text.component";
import GaborGrangerPlot from "../gabor-granger-plot/gabor-granger-plot.component";

const useStyles = createStyles((theme) => ({

    plotWrapper: {
        flexGrow: '1',
        width: '100px',
        height: 'calc(100vh - 120px)',
        minWidth: '300px',
        position: 'relative',
        [theme.fn.smallerThan('sm')]: {
            height: 'calc(100vh - 165px)',
        },
    },

}));

const getMargins = (isSmall, isMedium) => {
    if (isSmall) return { top: 20, right: 25, bottom: 20, left: 25 };
    if (isMedium) return { top: 50, right: 50, bottom: 60, left: 50 };
    return { top: 70, right: 100, bottom: 70, left: 100 };
}

const SurveyPlot = forwardRef(({
    surveyResponse,
    historyShown,
    isEmbeddingPlot,
    embeddingData,
    selectedEmbeddingIndex,
    shownQuestionIndices,
    isSmall,
    isMedium,
    hasMouse,
    similarQuestionData,
    similarDataPlotPrimary,
    setSimilarDataPlotPrimary,
    isFittingTSNE,
    closestTextsAndOptions
}, containerRef) => {

    const { classes } = useStyles();

    // const containerRef = useRef(null);
    const { width, height } = useResize(containerRef, [historyShown]);

    const margins = getMargins(isSmall, isMedium);
    const innerWidth = width - (margins.left + margins.right);
    const innerHeight = height - (margins.top + margins.bottom);

    const dimensions = { width, height, innerWidth, innerHeight, margins }

    const [oldDimensions, setOldDimensions] = useState({});
    const animateTransition = objectsAreEqual(oldDimensions, { innerWidth, innerHeight, height, width });

    useEffect(() => {
        setOldDimensions({ innerWidth, innerHeight, height, width });
    }, [width, height, innerWidth, innerHeight]);

    const isPanel = surveyResponse.surveyYears && surveyResponse.surveyYears[0] !== surveyResponse.surveyYears[1];
    const noSurveyResponse = isEmpty(surveyResponse);

    return (
        <div ref={containerRef} className={classes.plotWrapper}>
            {
                noSurveyResponse ?
                    <SvgPlotWrapper dimensions={dimensions}>
                        <LogoWithText innerWidth={dimensions.innerWidth} innerHeight={dimensions.innerHeight} />
                    </SvgPlotWrapper>
                    :
                    isEmbeddingPlot ?
                        <InvestigationsPlot
                            dimensions={dimensions}
                            isSmall={isSmall}
                            isMedium={isMedium}
                            embeddingData={embeddingData}
                            selectedEmbeddingIndex={selectedEmbeddingIndex}
                            shownQuestionIndices={shownQuestionIndices}
                            similarQuestionData={similarQuestionData}
                            hasMouse={hasMouse}
                            similarDataPlotPrimary={similarDataPlotPrimary}
                            setSimilarDataPlotPrimary={setSimilarDataPlotPrimary}
                            closestTextsAndOptions={closestTextsAndOptions}
                            isFittingTSNE={isFittingTSNE}
                        />
                        :
                        surveyResponse.surveyType === 'GGS' ?
                            <GaborGrangerPlot
                                dimensions={dimensions}
                                surveyResponse={surveyResponse}
                                isSmall={isSmall}
                                isMedium={isMedium}
                                hasMouse={hasMouse}
                                animateTransition={animateTransition}
                            />
                            :
                            isPanel ?
                                <LinePlot
                                    dimensions={dimensions}
                                    surveyResponse={surveyResponse}
                                    isSmall={isSmall}
                                    isMedium={isMedium}
                                    animateTransition={animateTransition}
                                    hasMouse={hasMouse}
                                    plotType='estimated'
                                />
                                :
                                <BarPlot
                                    dimensions={dimensions}
                                    surveyResponse={surveyResponse}
                                    isSmall={isSmall}
                                    isMedium={isMedium}
                                    animateTransition={animateTransition}
                                    hasMouse={hasMouse}
                                    plotType='estimated'
                                />
            }
        </div>
    )
});

export default SurveyPlot;